/* eslint-disable max-len */
import type { TefComsCustomerAttribute } from '@mfe-4cs/components/tef-coms-customer-attribute';
import { CustomerAttribute, CustomerAttributeGroupItem } from '@mfec/customerAttributes/types';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { TefComsCustomerAttributeBase } from '@mfec/customerAttributes/TefComsCustomerAttributeBase';
import { action } from 'mobx';
import { hasContent } from '@mfec/lit/hasContent';
import { localDate, localTime } from '../util';

export function getCustomerAttributesWithBulletpointTemplateResult(customerAttribute: CustomerAttribute, type: 'standard' | 'extra') {
  return customerAttribute[type]!.items.map((item: CustomerAttributeGroupItem) => html`
    <one-list-item>${unsafeHTML(item.title)}</one-list-item>
  `);
}

export function getCustomerAttributesWithIconTemplateResult(customerAttribute: CustomerAttribute) {
  // Here granted=undefined should NOT be displayed
  return customerAttribute.extra!.items.filter((item) => item.display).map((item: CustomerAttributeGroupItem) => html`
    <one-list-item>
      <one-icon slot="icon" name="${item.granted ? 'check' : 'cancel'}" variant="outline" size="xs"></one-icon>
      ${unsafeHTML(item.title)}
    </one-list-item>
  `);
}

export function getCustomerAttributesWithCheckboxTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, customerAttribute: CustomerAttribute) {
  return customerAttribute.extra!.items.map((item: CustomerAttributeGroupItem) => html`
    <one-cluster gap="xs" wrap="noWrap">
      <one-checkbox .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}" @click=${action(() => { tefComsCustomerAttribute.save(); })}></one-checkbox>
      <one-text as="span">${unsafeHTML(item.title)}</one-text>
    </one-cluster>
  `);
}

export function getExtraCustomerAttributeItemsTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (!customerAttribute.extra) return [];

  const blocks = [html`<one-text strong>${unsafeHTML(customerAttribute.extra.title || 'Zusatzprodukte')}</one-text>`];

  switch (itemsDisplayStyle) {
    case 'bulletpoint':
      blocks.push(html`
        <one-list icon="bullet-ul-list" icon-variant="solid">
          ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'extra')}
        </one-list>
      `);
      break;
    case 'icon':
      blocks.push(html`<one-list>${getCustomerAttributesWithIconTemplateResult(customerAttribute)}</one-list>`);
      break;
    case 'checkbox':
      blocks.push(html`${getCustomerAttributesWithCheckboxTemplateResult(tefComsCustomerAttribute, customerAttribute)}`);
      break;
    default:
  }

  return blocks;
}

export function getLastChangeTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (itemsDisplayStyle !== 'bulletpoint') {
    return html`
      <one-list-item size="xs" align-items="center">
        <one-icon name="clock" variant="outline" slot="icon" size="xs"></one-icon>
        <one-content>
          <one-text size="xs">
            Letzte Änderung am ${localDate(customerAttribute.date)} um ${localTime(customerAttribute.date)} Uhr
          </one-text>
        </one-content>
      </one-list-item>
    `;
  } return null;
}

export function getButtonsTemplateResult(comp: TefComsCustomerAttributeBase, mode: string) {
  const hideModal = () => {
    const modal : HTMLElement & { hide?: () => void } = comp.mainComponentNode.shadowRoot!.getElementById(`tef-coms-customer-attribute-modal-${comp.id}`)!;
    // @ts-expect-error "Works"
    modal.hide();
  };
  if (mode === 'checkbox') {
    return html`
      <one-button size="small" color="default" variant="solid" @click=${action(() => { comp.save(); hideModal(); })} data-testid="cattr-button-action-save">
        Weiter
      </one-button>
      <one-spacer size="fill"></one-spacer>
      <one-button size="small" color="default" variant="solid" @click=${() => { comp.cancel(); hideModal(); }} data-testid="cattr-button-action-cancel">
        Abbrechen
      </one-button>
    `;
  } return html`
    <one-button size="small" color="default" variant="solid" @click=${hideModal} data-testid="cattr-button-action-back">
      Zurück
    </one-button>
  `;
}

export const tefComsCustomerAttributeModalDialog = function render(
  this: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
  itemsDisplayStyle: string,
  template$: HTMLTemplateElement,
) {
  const title = (customerAttribute.title || template$.content.textContent || '').replace('O2', 'O<sub>2</sub>');

  return html`
  <one-modal id="tef-coms-customer-attribute-modal-${this.id}" closable="" size="md" variant="default">
    <span slot="label">${unsafeHTML(title)}</span>
    <one-stack gap="md">
      ${hasContent(customerAttribute.header) ? unsafeHTML(`<one-text data-testid="cattr-header">${customerAttribute.header}</one-text>`) : nothing}
      <one-text strong>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</one-text>
      <one-list icon="bullet-ul-list" icon-variant="solid">
        ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}
      </one-list>
      ${getExtraCustomerAttributeItemsTemplateResult(this, customerAttribute, itemsDisplayStyle)}
      ${getLastChangeTemplateResult(customerAttribute, itemsDisplayStyle)}
      <one-text as="p">${unsafeHTML(customerAttribute.footer)}</one-text>
    </one-stack>
    <one-button-group slot="footer">${getButtonsTemplateResult(this, itemsDisplayStyle)}</one-button-group>
  </one-modal>
  `;
};

export const tefComsCustomerAttributeModalTrigger = function render(
  this: TefComsCustomerAttribute,
  template$: HTMLTemplateElement,
) {
  const content = (template$.content.textContent || '').replace('O2', 'O<sub>2</sub>');

  const showModal = () => {
    const modal : HTMLElement & { show?: () => void } = this.mainComponentNode.shadowRoot!.getElementById(`tef-coms-customer-attribute-modal-${this.id}`)!;
    // @ts-expect-error "Works"
    modal.show();
  };

  const oneLink = html`<one-link 
    as="span" 
    color="prominent"
    id="tef-coms-customer-attribute-${this.id}" 
    role="button" 
    aria-haspopup="dialog"
    aria-controls="tef-coms-customer-attribute-modal-${this.id}"
    @click=${showModal}>
    ${unsafeHTML(content)}
  </one-link>`;

  if (this.isModifiedFromInitial) {
    return html`<one-paper background="legal-1" padding="xs" text-color="make-links-blau" style="display: inline-block;" role="dialog">${oneLink}</one-paper>`;
  }
  return oneLink;
};
